import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type SalaryAdvanceLineStatus = "active" | "deducted" | "cancelled";

export type EmployeeSalaryAdvanceLine = {
  id: string;
  batchId: string;
  employeeId: string;
  employeeName: string | null;
  advanceAmount: string;
  periodYear: number;
  periodMonth: number;
  reason: string | null;
  status: SalaryAdvanceLineStatus;
  salaryId: string | null;
  deductedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AdvanceSalaryBatch = {
  batchId: string;
  employeeId: string;
  employeeName: string | null;
  lines: EmployeeSalaryAdvanceLine[];
  totalAmount: number;
  canDelete: boolean;
  createdAt: string;
  updatedAt: string;
};

export type AdvanceSalaryFormMeta = {
  grossSalary: number;
  blockedPeriods: Array<{ year: number; month: number }>;
  reservedPeriods: Array<{ year: number; month: number }>;
};

type PaginatedResponse = {
  data?: Record<string, unknown>[];
  meta?: { current_page?: number; last_page?: number; per_page?: number; total?: number };
};

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function mapStatus(raw: unknown): SalaryAdvanceLineStatus {
  const s = str(raw);
  if (s === "deducted") return "deducted";
  if (s === "cancelled") return "cancelled";
  return "active";
}

function mapLine(raw: Record<string, unknown>): EmployeeSalaryAdvanceLine {
  return {
    id: str(raw.id),
    batchId: str(raw.batch_id),
    employeeId: str(raw.employee_id),
    employeeName: raw.employee_name != null ? str(raw.employee_name) : null,
    advanceAmount: str(raw.advance_amount),
    periodYear: Number(raw.period_year) || 0,
    periodMonth: Number(raw.period_month) || 0,
    reason: raw.reason != null ? str(raw.reason) : null,
    status: mapStatus(raw.status),
    salaryId: raw.salary_id != null ? str(raw.salary_id) : null,
    deductedAt: raw.deducted_at != null ? str(raw.deducted_at) : null,
    createdAt: str(raw.created_at),
    updatedAt: str(raw.updated_at),
  };
}

export function groupAdvanceSalaryBatches(lines: EmployeeSalaryAdvanceLine[]): AdvanceSalaryBatch[] {
  const map = new Map<string, EmployeeSalaryAdvanceLine[]>();
  for (const line of lines) {
    const key = line.batchId || line.id;
    const bucket = map.get(key) ?? [];
    bucket.push(line);
    map.set(key, bucket);
  }

  return Array.from(map.entries())
    .map(([batchId, batchLines]) => {
      const sorted = [...batchLines].sort((a, b) => a.id.localeCompare(b.id));
      const totalAmount = sorted.reduce((sum, row) => sum + (Number(row.advanceAmount.replace(/,/g, "")) || 0), 0);
      const canDelete = sorted.every((row) => row.status !== "deducted");
      const updatedAt = sorted.reduce((max, row) => (row.updatedAt > max ? row.updatedAt : max), sorted[0]?.updatedAt ?? "");
      const createdAt = sorted.reduce((min, row) => (row.createdAt < min ? row.createdAt : min), sorted[0]?.createdAt ?? "");
      const head = sorted[0];

      return {
        batchId,
        employeeId: head?.employeeId ?? "",
        employeeName: head?.employeeName ?? null,
        lines: sorted,
        totalAmount,
        canDelete,
        createdAt,
        updatedAt,
      };
    })
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function salaryAdvancesApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong.";
}

export async function listSalaryAdvancesForEmployee(params: {
  employeeId: string;
  page?: number;
  perPage?: number;
}): Promise<{ rows: EmployeeSalaryAdvanceLine[]; meta: { currentPage: number; lastPage: number; perPage: number; total: number } }> {
  const response = await api.get<PaginatedResponse>(`/api/v1/employees/${encodeURIComponent(params.employeeId)}/salary-advances`, {
    params: {
      page: params.page,
      per_page: params.perPage,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  const meta = response.data?.meta ?? {};
  return {
    rows: list.map((r) => mapLine(r as Record<string, unknown>)),
    meta: {
      currentPage: meta.current_page ?? 1,
      lastPage: meta.last_page ?? 1,
      perPage: meta.per_page ?? params.perPage ?? 20,
      total: meta.total ?? list.length,
    },
  };
}

export async function listAllSalaryAdvances(params: {
  page?: number;
  perPage?: number;
}): Promise<{ rows: EmployeeSalaryAdvanceLine[]; meta: { currentPage: number; lastPage: number; perPage: number; total: number } }> {
  const response = await api.get<PaginatedResponse>("/api/v1/salary-advances", {
    params: {
      page: params.page,
      per_page: params.perPage,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  const meta = response.data?.meta ?? {};
  return {
    rows: list.map((r) => mapLine(r as Record<string, unknown>)),
    meta: {
      currentPage: meta.current_page ?? 1,
      lastPage: meta.last_page ?? 1,
      perPage: meta.per_page ?? params.perPage ?? 20,
      total: meta.total ?? list.length,
    },
  };
}

export async function fetchAdvanceSalaryFormMeta(employeeId: string): Promise<AdvanceSalaryFormMeta> {
  const response = await api.get<{ data?: Record<string, unknown> }>("/api/v1/salary-advances/form-meta", {
    params: { employee_id: employeeId },
  });
  const raw = response.data?.data ?? {};
  const blocked = Array.isArray(raw.blocked_periods) ? raw.blocked_periods : [];
  const reserved = Array.isArray(raw.reserved_periods) ? raw.reserved_periods : [];

  return {
    grossSalary: Number(raw.gross_salary) || 0,
    blockedPeriods: blocked.map((p) => ({
      year: Number((p as { year?: unknown }).year) || 0,
      month: Number((p as { month?: unknown }).month) || 0,
    })),
    reservedPeriods: reserved.map((p) => ({
      year: Number((p as { year?: unknown }).year) || 0,
      month: Number((p as { month?: unknown }).month) || 0,
    })),
  };
}

export async function createAdvanceSalaryBatch(params: {
  employeeId: string;
  lines: Array<{
    advanceAmount: number | string;
    periodYear: number;
    periodMonth: number;
    reason?: string | null;
  }>;
}): Promise<{ lines: EmployeeSalaryAdvanceLine[]; message: string }> {
  const response = await api.post<{ message?: string; data?: Record<string, unknown>[] }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/salary-advances`,
    {
      lines: params.lines.map((line) => ({
        advance_amount: line.advanceAmount,
        period_year: line.periodYear,
        period_month: line.periodMonth,
        reason: line.reason?.trim() || null,
      })),
    },
  );
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  return {
    lines: list.map((r) => mapLine(r as Record<string, unknown>)),
    message: str(response.data?.message) || "Advance salary recorded.",
  };
}

export async function deleteAdvanceSalaryBatch(params: {
  employeeId: string;
  batchId: string;
}): Promise<{ message: string }> {
  const response = await api.delete<{ message?: string }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/salary-advances/batch/${encodeURIComponent(params.batchId)}`,
  );
  return {
    message: str(response.data?.message) || "Advance salary record deleted.",
  };
}

export function periodKey(year: number, month: number): string {
  return `${year}-${month}`;
}

export function periodLabel(year: number, month: number, months: readonly { v: number; label: string }[]): string {
  const m = months.find((x) => x.v === month);
  return `${m?.label ?? month} ${year}`;
}
